/**
 * Détection de la plateforme d'exécution.
 *
 * Le front peut tourner dans un navigateur (web/PWA) ou dans le conteneur natif
 * Capacitor (WebView iOS/Android). On lit le global `Capacitor` injecté par le
 * runtime natif plutôt que d'importer `@capacitor/core` — ainsi le bundle web ne
 * dépend pas des paquets Capacitor (ceux-ci vivent dans le module `mobile/`).
 */

interface CapacitorGlobal {
	isNativePlatform?: () => boolean;
	getPlatform?: () => string;
}

function capacitor(): CapacitorGlobal | undefined {
	if (typeof window === 'undefined') return undefined;
	return (window as unknown as { Capacitor?: CapacitorGlobal }).Capacitor;
}

/**
 * `true` si le code s'exécute dans l'app native Capacitor (iOS/Android).
 *
 * Blindé : le global `Capacitor` est injecté par un tiers (le runtime natif) ; on
 * ne fait aucune hypothèse sur sa forme. Toute anomalie (méthode absente, valeur
 * non-fonction usurpée sur le web…) retombe sur `false` — jamais d'exception qui
 * remonterait dans le `onMount` du layout web.
 */
export function isNativePlatform(): boolean {
	try {
		return capacitor()?.isNativePlatform?.() === true;
	} catch {
		return false;
	}
}

/** Plateforme courante : `ios`, `android` ou `web` (repli `web` sur anomalie). */
export function getPlatform(): 'ios' | 'android' | 'web' {
	try {
		const platform = capacitor()?.getPlatform?.();
		return platform === 'ios' || platform === 'android' ? platform : 'web';
	} catch {
		return 'web';
	}
}
